import i18n from '@/i18n';
import {
  startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  subDays, subWeeks, subMonths, differenceInMilliseconds, format, addDays, addWeeks, addMonths,
} from 'date-fns';

export type SaleItemLike = {
  product_id?: string | null;
  product_name?: string | null;
  quantity?: number | string | null;
  total?: number | string | null;
  products?: { name?: string | null; category?: string | null } | null;
};

export type SaleLike = {
  id?: string;
  created_at: string;
  total_amount: number | string;
  payment_method?: string | null;
  is_murabaha?: boolean | null;
  status?: string | null;
  branch_id?: string | null;
  cashier_id?: string | null;
  sale_items?: SaleItemLike[] | null;
};

export type DateRange = { from: Date; to: Date };
export type Granularity = 'day' | 'week' | 'month';
export type TrendPoint = { key: string; label: string; sum: number; count: number };

export const num = (v: unknown) => Number(v ?? 0) || 0;

/** Faqat yakunlangan savdolar hisobga olinadi (qaytarilgan/bekor qilingan chiqariladi). */
export const isCountedSale = (s: SaleLike) => (s.status ?? 'completed') === 'completed';

export const PERIOD_KEYS = ['today', '7days', 'week', '30days', 'month', 'all', 'custom'] as const;
export type PeriodKey = (typeof PERIOD_KEYS)[number];

/** Tanlangan davr va u bilan solishtiriladigan oldingi davr. */
export function resolvePeriod(
  period: PeriodKey,
  now: Date = new Date(),
  custom?: Partial<DateRange>,
): { range: DateRange; previous: DateRange | null } {
  const mk = (from: Date, to: Date): DateRange => ({ from, to });
  let range: DateRange;
  switch (period) {
    case 'today':
      range = mk(startOfDay(now), endOfDay(now));
      break;
    case '7days':
      range = mk(startOfDay(subDays(now, 6)), endOfDay(now));
      break;
    case 'week':
      range = mk(startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 }));
      break;
    case '30days':
      range = mk(startOfDay(subDays(now, 29)), endOfDay(now));
      break;
    case 'month':
      range = mk(startOfMonth(now), endOfMonth(now));
      break;
    case 'custom':
      range = mk(
        startOfDay(custom?.from ?? subDays(now, 29)),
        endOfDay(custom?.to ?? now),
      );
      break;
    default:
      range = mk(new Date(0), endOfDay(now));
  }

  if (period === 'all') return { range, previous: null };
  if (period === 'week') {
    return {
      range,
      previous: mk(startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 })),
    };
  }
  if (period === 'month') {
    return { range, previous: mk(startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))) };
  }
  const span = differenceInMilliseconds(range.to, range.from);
  const prevTo = new Date(range.from.getTime() - 1);
  return { range, previous: mk(new Date(prevTo.getTime() - span), prevTo) };
}

export function filterSales(
  sales: SaleLike[],
  opts: { range?: DateRange | null; branchId?: string | null; onlyCounted?: boolean } = {},
): SaleLike[] {
  const { range, branchId, onlyCounted = true } = opts;
  return sales.filter((s) => {
    if (onlyCounted && !isCountedSale(s)) return false;
    if (branchId && s.branch_id !== branchId) return false;
    if (range) {
      const t = new Date(s.created_at).getTime();
      if (t < range.from.getTime() || t > range.to.getTime()) return false;
    }
    return true;
  });
}

export const sumSales = (sales: SaleLike[]) => sales.reduce((acc, s) => acc + num(s.total_amount), 0);

export function pctChange(current: number, previous: number): number | null {
  if (!previous) return current > 0 ? 100 : null;
  return ((current - previous) / previous) * 100;
}


/** Bo'sh oraliqlar ham 0 bilan to'ldiriladi. */
export function buildTrend(
  sales: SaleLike[],
  granularity: Granularity,
  buckets: number,
  now: Date = new Date(),
): TrendPoint[] {
  const startOf = (d: Date) =>
    granularity === 'day' ? startOfDay(d)
      : granularity === 'week' ? startOfWeek(d, { weekStartsOn: 1 })
        : startOfMonth(d);
  const step = (d: Date, n: number) =>
    granularity === 'day' ? addDays(d, n) : granularity === 'week' ? addWeeks(d, n) : addMonths(d, n);

  const points: TrendPoint[] = [];
  const first = step(startOf(now), -(buckets - 1));
  for (let i = 0; i < buckets; i++) {
    const d = step(first, i);
    points.push({
      key: format(d, 'yyyy-MM-dd'),
      label: granularity === 'day' ? format(d, 'dd.MM')
        : granularity === 'week' ? format(d, 'dd.MM')
          : format(d, 'MM.yyyy'),
      sum: 0,
      count: 0,
    });
  }
  const index = new Map(points.map((p, i) => [p.key, i]));
  for (const s of sales) {
    const k = format(startOf(new Date(s.created_at)), 'yyyy-MM-dd');
    const i = index.get(k);
    if (i === undefined) continue;
    points[i].sum += num(s.total_amount);
    points[i].count += 1;
  }
  return points;
}

export function salesByHour(sales: SaleLike[]): TrendPoint[] {
  const points: TrendPoint[] = Array.from({ length: 24 }, (_, h) => ({
    key: String(h), label: `${String(h).padStart(2, '0')}:00`, sum: 0, count: 0,
  }));
  for (const s of sales) {
    const h = new Date(s.created_at).getHours();
    points[h].sum += num(s.total_amount);
    points[h].count += 1;
  }
  return points;
}

export function salesByWeekday(sales: SaleLike[]): TrendPoint[] {
  const points: TrendPoint[] = Array.from({ length: 7 }, (_, i) => ({ key: String(i), label: i18n.t(`misc_weekdays_short.${i}`), sum: 0, count: 0 }));
  for (const s of sales) {
    const js = new Date(s.created_at).getDay(); // 0 = Yakshanba
    const i = (js + 6) % 7;
    points[i].sum += num(s.total_amount);
    points[i].count += 1;
  }
  return points;
}

export type ProductStat = { productId: string; name: string; quantity: number; sum: number };

export function topProducts(sales: SaleLike[], by: 'sum' | 'quantity' = 'sum', limit = 10): ProductStat[] {
  const map = new Map<string, ProductStat>();
  for (const s of sales) {
    for (const it of s.sale_items ?? []) {
      const id = it.product_id ?? it.product_name ?? 'unknown';
      const name = it.product_name || it.products?.name || 'Noma\'lum';
      const cur = map.get(id) ?? { productId: id, name, quantity: 0, sum: 0 };
      cur.quantity += num(it.quantity);
      cur.sum += num(it.total);
      map.set(id, cur);
    }
  }
  return [...map.values()].sort((a, b) => b[by] - a[by]).slice(0, limit);
}

export type CategoryStat = { category: string; quantity: number; sum: number; pct: number };

export function salesByCategory(sales: SaleLike[], limit = 8): CategoryStat[] {
  const map = new Map<string, CategoryStat>();
  let total = 0;
  for (const s of sales) {
    for (const it of s.sale_items ?? []) {
      const category = it.products?.category?.trim() || 'Kategoriyasiz';
      const cur = map.get(category) ?? { category, quantity: 0, sum: 0, pct: 0 };
      cur.quantity += num(it.quantity);
      cur.sum += num(it.total);
      total += num(it.total);
      map.set(category, cur);
    }
  }
  return [...map.values()]
    .map((c) => ({ ...c, pct: total > 0 ? (c.sum / total) * 100 : 0 }))
    .sort((a, b) => b.sum - a.sum)
    .slice(0, limit);
}

/** Davr ichida sotilmagan, lekin faol mahsulotlar. */
export function slowMovingProducts(
  sales: SaleLike[],
  products: { id: string | number; name: string; sku?: string | null; is_active?: boolean | null }[],
  limit = 10,
) {
  const sold = new Set<string>();
  for (const s of sales) for (const it of s.sale_items ?? []) if (it.product_id) sold.add(it.product_id);
  // API id'lari son, savdo qatorlari satr — solishtirish satr sifatida
  return products.filter((p) => (p.is_active ?? true) && !sold.has(String(p.id))).slice(0, limit);
}

export function refundedSummary(sales: SaleLike[], opts: { range?: DateRange | null; branchId?: string | null } = {}) {
  const all = filterSales(sales, { ...opts, onlyCounted: false });
  const bad = all.filter((s) => !isCountedSale(s));
  return { count: bad.length, sum: sumSales(bad) };
}
